import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import LogoImage from '../../components/ui/LogoImage';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

// ── Forgot Password Page ──────────────────────────────────────
export function AdminForgotPasswordPage() {
  const [email, setEmail]   = useState('');
  const [sent, setSent]     = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await authAPI.forgotPassword({ email });
      setSent(true);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen pattern-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <LogoImage size={72} className="rounded-full border-4 border-cyan-400 shadow-xl" />
          </div>
          <h1 className="text-2xl font-bold text-navy-800">JNF Admin</h1>
          <p className="text-gray-500 text-sm mt-1">Reset your admin password</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          {sent ? (
            <div className="text-center py-4">
              <div className="text-5xl mb-4">📧</div>
              <h2 className="text-lg font-bold text-navy-800 mb-2">Reset Link Sent</h2>
              <p className="text-gray-500 text-sm mb-1">
                If an account exists for <strong>{email}</strong>, a reset link has been sent.
              </p>
              <p className="text-gray-400 text-xs mb-6">Check your inbox and spam folder.</p>
              <Link to="/admin/login" className="btn-primary inline-block text-sm py-2.5 px-6">
                ← Back to Login
              </Link>
            </div>
          ) : (
            <>
              <p className="text-gray-500 text-sm mb-5">
                Enter your admin email address and we'll send you a link to reset your password.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="form-label">Email Address</label>
                  <input
                    required type="email"
                    className="form-input"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="admin@jnaft.org"
                  />
                </div>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary w-full flex items-center justify-center gap-2 py-2.5"
                >
                  {saving
                    ? <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>Sending...</>
                    : '📧 Send Reset Link'}
                </button>
              </form>
            </>
          )}
        </div>

        <div className="text-center mt-5 space-y-1">
          <Link to="/admin/login" className="block text-gray-400 text-xs hover:text-navy-600 transition-colors">
            ← Back to Admin Login
          </Link>
          <Link to="/" className="block text-gray-300 text-xs hover:text-navy-500 transition-colors">
            ← Back to Website
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Reset Password Page ───────────────────────────────────────
export function AdminResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const token          = searchParams.get('token');
  const [form, setForm]   = useState({ password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showCf, setShowCf] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Password strength
  const checks = [
    form.password.length >= 8,
    /[A-Z]/.test(form.password),
    /[0-9]/.test(form.password),
    /[^A-Za-z0-9]/.test(form.password),
  ];
  const strength = checks.filter(Boolean).length;
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'][strength];
  const strengthColor = ['', 'bg-red-400', 'bg-amber-400', 'bg-blue-400', 'bg-green-500'][strength];
  const strengthText  = ['', 'text-red-500', 'text-amber-500', 'text-blue-500', 'text-green-600'][strength];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token)
      return toast.error('Invalid reset link. Please request a new one.');
    if (form.password !== form.confirm)
      return toast.error('Passwords do not match.');
    if (form.password.length < 8)
      return toast.error('Password must be at least 8 characters.');

    setSaving(true);
    try {
      await authAPI.resetPassword({ token, password: form.password });
      toast.success('Password reset successfully!');
      navigate('/admin/login');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reset failed. The link may have expired.');
    } finally {
      setSaving(false);
    }
  };

  if (!token) return (
    <div className="min-h-screen pattern-bg flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <div className="text-4xl mb-4">❌</div>
        <h2 className="text-lg font-bold text-navy-800 mb-2">Invalid Reset Link</h2>
        <p className="text-gray-500 text-sm mb-5">This reset link is missing or invalid. Please request a new one.</p>
        <Link to="/admin/forgot-password" className="btn-primary inline-block text-sm py-2.5 px-6">Request New Link</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pattern-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <LogoImage size={72} className="rounded-full border-4 border-cyan-400 shadow-xl" />
          </div>
          <h1 className="text-2xl font-bold text-navy-800">Set New Password</h1>
          <p className="text-gray-500 text-sm mt-1">Choose a strong new admin password</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <form onSubmit={handleSubmit} className="space-y-4">

            {/* New password */}
            <div>
              <label className="form-label">New Password *</label>
              <div className="relative">
                <input
                  required
                  type={showPw ? 'text' : 'password'}
                  className="form-input pr-10"
                  value={form.password}
                  onChange={e => set('password', e.target.value)}
                  placeholder="Minimum 8 characters"
                />
                <button type="button" onClick={() => setShowPw(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-navy-600" tabIndex={-1}>
                  {showPw ? <EyeSlashIcon className="h-4 w-4"/> : <EyeIcon className="h-4 w-4"/>}
                </button>
              </div>
              {/* Strength bar */}
              {form.password && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1,2,3,4].map(i => (
                      <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i <= strength ? strengthColor : 'bg-gray-200'}`}/>
                    ))}
                  </div>
                  <p className={`text-xs font-medium ${strengthText}`}>{strengthLabel}</p>
                </div>
              )}
            </div>

            {/* Confirm password */}
            <div>
              <label className="form-label">Confirm Password *</label>
              <div className="relative">
                <input
                  required
                  type={showCf ? 'text' : 'password'}
                  className="form-input pr-10"
                  value={form.confirm}
                  onChange={e => set('confirm', e.target.value)}
                  placeholder="Repeat new password"
                />
                <button type="button" onClick={() => setShowCf(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-navy-600" tabIndex={-1}>
                  {showCf ? <EyeSlashIcon className="h-4 w-4"/> : <EyeIcon className="h-4 w-4"/>}
                </button>
              </div>
              {/* Match indicator */}
              {form.confirm && (
                <p className={`text-xs font-medium mt-1 ${form.password === form.confirm ? 'text-green-600' : 'text-red-500'}`}>
                  {form.password === form.confirm ? '✅ Passwords match' : '❌ Passwords do not match'}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={saving}
              className="btn-primary w-full flex items-center justify-center gap-2 py-2.5 mt-2"
            >
              {saving
                ? <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>Resetting...</>
                : '🔐 Reset Password'}
            </button>
          </form>
        </div>

        <p className="text-center mt-5">
          <Link to="/admin/login" className="text-gray-400 text-xs hover:text-navy-600 transition-colors">
            ← Back to Admin Login
          </Link>
        </p>
      </div>
    </div>
  );
}
